import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, X, Gift, Mail, ArrowRight, Shield, Clock, Target, Database } from 'lucide-react';
import Header from "../../Components/Header";
import Footer from "../../Components/Footer";
import api from "../../api";
import TagManager from "react-gtm-module";

const CaseStudiesLanding = () => {
  const [downloadForm, setDownloadForm] = useState({
    email: "",
    name: "",
    company: "",
    agreeToTerms: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [hasReceivedEbook, setHasReceivedEbook] = useState(false);
  const [showMarketingModal, setShowMarketingModal] = useState(false);

  // 사례집 받은 사람은 이탈 시 모달 안뜨게
  useEffect(() => {
    const ebookReceived = localStorage.getItem('caseStudiesReceived');
    if (ebookReceived === 'true') {
      setHasReceivedEbook(true);
    }
  }, []);

  // Exit Intent 감지
  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !showExitPopup && !hasReceivedEbook && !showSuccessModal) {
        setShowExitPopup(true);
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [showExitPopup, hasReceivedEbook, showSuccessModal]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDownloadForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDownload = async (e) => {
    e?.preventDefault();

    if (!downloadForm.email) {
      alert('이메일 주소를 입력해주세요.');
      return;
    }

    if (!downloadForm.agreeToTerms) {
      alert('사례집을 받으시려면 개인정보 처리방침 및 마케팅 정보 수신에 동의해주세요.');
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await api.post('marketing/casestudy-subscribe/', {
        email: downloadForm.email,
        name: downloadForm.name,
        company_name: downloadForm.company
      });

      setShowSuccessModal(true);
      setHasReceivedEbook(true);
      localStorage.setItem('caseStudiesReceived', 'true');

      // GTM 이벤트 즉시 전송 👈 여기에 추가
      window.dataLayer = window.dataLayer || [];
      TagManager.dataLayer({
        dataLayer: {
          event: "casestudy_download",
          content_name: "service_casestudy",
          content_type: "pdf",
          page_type: "casestudy_landing",
        },
      });

      // Exit 팝업이 열려있다면 닫기
      if (showExitPopup) {
        setShowExitPopup(false);
      }
    } catch (error) {
      console.error('사례집 구독 중 오류 발생:', error);

      if (error.response) {
        if (error.response.status === 400) {
          alert('입력하신 정보를 다시 확인해주세요.');
        } else if (error.response.status === 500) {
          alert('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
        } else {
          alert('사례집 신청 중 오류가 발생했습니다. 다시 시도해주세요.');
        }
      } else if (error.request) {
        alert('네트워크 연결을 확인해주세요.');
      } else {
        alert('오류가 발생했습니다. 페이지를 새로고침 후 다시 시도해주세요.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="h-16 bg-white"></div>

      {/* 성공 모달 */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full relative animate-bounce-in">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mb-4 sm:mb-6">
                <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-green-500" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-gray-900">
                사례집 신청이 완료되었습니다!
              </h3>
              <div className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                <p className="text-base sm:text-lg text-gray-700">
                  감사합니다, {downloadForm.name}님!
                </p>
                <p className="text-sm sm:text-base text-gray-600">
                  입력하신 이메일 주소로<br />
                  <span className="font-semibold text-blue-600">{downloadForm.email}</span><br />
                  사례집이 발송되었습니다.
                </p>
                <div className="bg-blue-50 rounded-lg p-3 sm:p-4 mt-3 sm:mt-4">
                  <p className="text-xs sm:text-sm text-blue-800">
                    💡 이메일이 도착하지 않았다면 스팸함을 확인해주세요.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowSuccessModal(false)}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exit Intent 팝업 */}
      {showExitPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-lg w-full relative animate-bounce-in max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowExitPopup(false)}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-gray-600 z-10"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
            <div className="text-center">
              <Gift className="w-12 h-12 sm:w-16 sm:h-16 text-blue-600 mx-auto mb-3 sm:mb-4" />
              <h3 className="text-xl sm:text-2xl font-bold mb-2">잠깐! 떠나지 마세요</h3>
              <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6">
                지금 이메일을 입력하시면<br />
                <span className="font-bold text-blue-600">5개 기업의 실제 성과 사례</span>를 바로 받아보실 수 있습니다!
              </p>

              <form onSubmit={handleDownload} className="space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-left">
                    회사명 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm sm:text-base"
                    placeholder="회사명을 입력하세요"
                    value={downloadForm.company}
                    onChange={(e) => setDownloadForm({ ...downloadForm, company: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-left">
                    이름 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm sm:text-base"
                    placeholder="홍길동"
                    value={downloadForm.name}
                    onChange={(e) => setDownloadForm({ ...downloadForm, name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-left">
                    업무용 이메일 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm sm:text-base"
                    placeholder="your@email.com"
                    value={downloadForm.email}
                    onChange={(e) => setDownloadForm({ ...downloadForm, email: e.target.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1 text-left">
                    * 정확한 이메일 주소를 입력해주세요. 사례집이 이 주소로 발송됩니다.
                  </p>
                </div>

                <div className="mt-3 sm:mt-4 text-left">
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      required
                      className="mt-1 mr-2 sm:mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
                      checked={downloadForm.agreeToTerms}
                      onChange={(e) => setDownloadForm({ ...downloadForm, agreeToTerms: e.target.checked })}
                    />
                    <div className="flex-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        <Link to="/privacy" className="text-blue-600 hover:underline">
                          개인정보 처리방침
                        </Link> 및 <br className="sm:hidden" />
                        <button
                          type="button"
                          onClick={() => setShowMarketingModal(true)}
                          className="text-blue-600 hover:underline"
                        >
                          마케팅 정보 수신
                        </button>
                        에&nbsp;동의합니다. <span className="text-red-500">*</span>
                      </span>
                      <div className="text-xs text-gray-500 mt-1">
                        사례집 발송을 위해 필수로 동의가 필요합니다.
                      </div>
                    </div>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-2.5 sm:py-3 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 text-sm sm:text-base"
                >
                  {isSubmitting ? '전송 중...' : '지금 이메일로 받기'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="pt-12 sm:pt-16 lg:pt-20 pb-10 sm:pb-12 bg-gradient-to-br from-blue-600 via-blue-500 to-purple-600 text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10">
          <div className="absolute top-0 right-0 w-[400px] sm:w-[600px] h-[400px] sm:h-[600px] bg-blue-500 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3"></div>
          <div className="absolute bottom-0 left-0 w-[400px] sm:w-[600px] h-[400px] sm:h-[600px] bg-purple-300 rounded-full blur-3xl translate-y-1/3 -translate-x-1/3"></div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="inline-block bg-white/20 backdrop-blur-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6">
              📖 무료 다운로드
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight px-2">
              A/B 테스트로 검증된<br />
              <span className="text-yellow-300">실제 성과 사례집</span>
            </h1>

            <p className="text-lg sm:text-xl lg:text-2xl text-blue-100 mb-3 sm:mb-4 px-2">
              5개 기업의 실제 A/B 테스트 전략과 성과
            </p>
            <p className="text-base sm:text-lg text-blue-200 mb-6 sm:mb-8 px-4">
              전환율 +54%, 매출 +3,500만원 등<br />
              데이터로 증명된 성공 스토리를<br className="sm:hidden" /> 지금 바로 확인하세요
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
              <a
                href="#download"
                className="inline-block bg-white text-blue-600 px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg shadow-2xl hover:bg-blue-50 transition-all hover:scale-105"
              >
                📥 무료로 다운로드하기
              </a>
            </div>

            <p className="text-xs sm:text-sm text-blue-200 mt-4 sm:mt-6 px-4">
              ✓ 이메일 등록만으로 즉시 다운로드 &nbsp;&nbsp; ✓ 29페이지 PDF 제공
            </p>
          </div>
        </div>
      </section>

      {/* 사례집 미리보기 */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">
              이런 내용이 담겨있습니다
            </h2>
            <p className="text-base sm:text-lg text-gray-600 px-4">
              실제 기업의 A/B 테스트 전략과 성과를<br className="sm:hidden" /> 상세히 분석했습니다
            </p>
          </div>

          <div className="space-y-4 sm:space-y-6">
            {/* 사례 1 */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md border-l-4 border-purple-500 hover:shadow-lg transition-shadow">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 sm:gap-3 mb-2">
                    <span className="bg-purple-500 text-white w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm flex-shrink-0">
                      01
                    </span>
                    <h3 className="text-lg sm:text-xl font-bold">페미닌 케어 브랜드 C사</h3>
                  </div>
                  <p className="text-sm sm:text-base text-gray-600 mb-2 sm:mb-3">
                    '임상시험' 대신 '약국 판매 1위'로 신뢰도를 높여<br className="sm:hidden" /> 전환율 20% 상승
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-purple-100 text-purple-700 px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm">
                      사회적 증거
                    </span>
                    <span className="bg-purple-100 text-purple-700 px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm">
                      헬스케어
                    </span>
                  </div>
                </div>
                <div className="sm:text-right sm:ml-4 flex sm:flex-col items-center sm:items-end gap-2">
                  <div className="text-2xl sm:text-3xl font-bold text-purple-600">+20%</div>
                  <div className="text-xs sm:text-sm text-gray-500">구매 전환율</div>
                </div>
              </div>
            </div>

            {/* 사례 2 */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md border-l-4 border-blue-500 hover:shadow-lg transition-shadow">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 sm:gap-3 mb-2">
                    <span className="bg-blue-500 text-white w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm flex-shrink-0">
                      02
                    </span>
                    <h3 className="text-lg sm:text-xl font-bold">코딩 교육 기업 N사</h3>
                  </div>
                  <p className="text-sm sm:text-base text-gray-600 mb-2 sm:mb-3">
                    플로팅 버튼과 혜택 문구로 신청률 +65%,<br className="sm:hidden" /> 결제율 +41% 달성
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-blue-100 text-blue-700 px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm">
                      CTA 최적화
                    </span>
                    <span className="bg-blue-100 text-blue-700 px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm">
                      에듀테크
                    </span>
                  </div>
                </div>
                <div className="sm:text-right sm:ml-4 flex sm:flex-col items-center sm:items-end gap-2">
                  <div className="text-2xl sm:text-3xl font-bold text-blue-600">+65%</div>
                  <div className="text-xs sm:text-sm text-gray-500">신청률</div>
                </div>
              </div>
            </div>

            {/* 사례 3 */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md border-l-4 border-amber-500 hover:shadow-lg transition-shadow">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 sm:gap-3 mb-2">
                    <span className="bg-amber-500 text-white w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm flex-shrink-0">
                      03
                    </span>
                    <h3 className="text-lg sm:text-xl font-bold">산삼 건강식품 브랜드 B사</h3>
                  </div>
                  <p className="text-sm sm:text-base text-gray-600 mb-2 sm:mb-3">
                    '효능' 대신 '6년근 프리미엄' 강조로 5060 타겟<br className="sm:hidden" /> 전환율 50% 상승
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-amber-100 text-amber-700 px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm">
                      프리미엄 전략
                    </span>
                    <span className="bg-amber-100 text-amber-700 px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm">
                      헬스케어
                    </span>
                  </div>
                </div>
                <div className="sm:text-right sm:ml-4 flex sm:flex-col items-center sm:items-end gap-2">
                  <div className="text-2xl sm:text-3xl font-bold text-amber-600">+50%</div>
                  <div className="text-xs sm:text-sm text-gray-500">전환율</div>
                </div>
              </div>
            </div>

            {/* 사례 4 */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md border-l-4 border-teal-500 hover:shadow-lg transition-shadow">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 sm:gap-3 mb-2">
                    <span className="bg-teal-500 text-white w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm flex-shrink-0">
                      04
                    </span>
                    <h3 className="text-lg sm:text-xl font-bold">혈당 개선 건강식품 D사</h3>
                  </div>
                  <p className="text-sm sm:text-base text-gray-600 mb-2 sm:mb-3">
                    '진심의 메시지'와 구체적 혜택 명시로<br className="sm:hidden" /> 구독률 40% 상승
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-teal-100 text-teal-700 px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm">
                      공감 메시지
                    </span>
                    <span className="bg-teal-100 text-teal-700 px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm">
                      헬스케어
                    </span>
                  </div>
                </div>
                <div className="sm:text-right sm:ml-4 flex sm:flex-col items-center sm:items-end gap-2">
                  <div className="text-2xl sm:text-3xl font-bold text-teal-600">+40%</div>
                  <div className="text-xs sm:text-sm text-gray-500">구독률</div>
                </div>
              </div>
            </div>

            {/* 사례 5 */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md border-l-4 border-purple-500 hover:shadow-lg transition-shadow">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 sm:gap-3 mb-2">
                    <span className="bg-purple-500 text-white w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm flex-shrink-0">
                      05
                    </span>
                    <h3 className="text-lg sm:text-xl font-bold">프리미엄 웰니스 브랜드</h3>
                  </div>
                  <p className="text-sm sm:text-base text-gray-600 mb-2 sm:mb-3">
                    '가입의 문턱'을 낮춰 신규 회원 가입률 54% 상승
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-purple-100 text-purple-700 px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm">
                      가입 최적화
                    </span>
                    <span className="bg-purple-100 text-purple-700 px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm">
                      웰니스
                    </span>
                  </div>
                </div>
                <div className="sm:text-right sm:ml-4 flex sm:flex-col items-center sm:items-end gap-2">
                  <div className="text-2xl sm:text-3xl font-bold text-purple-600">+54%</div>
                  <div className="text-xs sm:text-sm text-gray-500">회원가입률</div>
                </div>
              </div>
            </div>
          </div>

          {/* 통계 요약 */}
          <div className="mt-8 sm:mt-12 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl p-6 sm:p-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 text-center">
              <div>
                <div className="text-3xl sm:text-4xl font-bold mb-1 sm:mb-2">5개</div>
                <div className="text-xs sm:text-sm lg:text-base text-blue-100">실제 기업 사례</div>
              </div>
              <div>
                <div className="text-3xl sm:text-4xl font-bold mb-1 sm:mb-2">27</div>
                <div className="text-xs sm:text-sm lg:text-base text-blue-100">페이지 분량</div>
              </div>
              <div>
                <div className="text-3xl sm:text-4xl font-bold mb-1 sm:mb-2">평균 +42%</div>
                <div className="text-xs sm:text-sm lg:text-base text-blue-100">전환율 상승</div>
              </div>
              <div>
                <div className="text-3xl sm:text-4xl font-bold mb-1 sm:mb-2">무료</div>
                <div className="text-xs sm:text-sm lg:text-base text-blue-100">다운로드</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 다운로드 폼 */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-blue-600 via-blue-500 to-purple-600" id="download">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* 폼 */}
          <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 text-gray-900 shadow-2xl">
            <div className="text-center mb-6 sm:mb-8">
              <h3 className="text-xl sm:text-2xl font-bold mb-2">
                A/B 테스트 성과 사례집 받기
              </h3>
              {/* 이메일 발송 안내 */}
              <div className="bg-blue-50 rounded-lg px-3 sm:px-4 py-2 inline-flex items-center mt-3 sm:mt-4">
                <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2 flex-shrink-0" />
                <span className="text-xs sm:text-sm text-blue-800 font-medium">
                  입력하신 이메일로 사례집이 즉시 발송됩니다
                </span>
              </div>
            </div>

            <form onSubmit={handleDownload} className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  회사명 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="company"
                  value={downloadForm.company}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm sm:text-base"
                  placeholder="회사명을 입력하세요"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  이름 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={downloadForm.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm sm:text-base"
                  placeholder="홍길동"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  업무용 이메일 <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={downloadForm.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm sm:text-base"
                  placeholder="your@email.com"
                />
                {/* 이메일 필드 아래 추가 안내 */}
                <p className="text-xs text-gray-500 mt-1">
                  <span className="hidden sm:inline">* 정확한 이메일 주소를 입력해주세요. 사례집이 이 주소로 발송됩니다.</span>
                  <span className="sm:hidden">
                    * 정확한 이메일 주소를 입력해주세요.<br />
                    사례집이 이 주소로 발송됩니다.
                  </span>
                </p>
              </div>

              {/* 개인정보 및 마케팅 통합 동의 - 필수 */}
              <div className="mt-3 sm:mt-4">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    required
                    className="mt-1 mr-2 sm:mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
                    checked={downloadForm.agreeToTerms}
                    onChange={(e) => setDownloadForm({ ...downloadForm, agreeToTerms: e.target.checked })}
                  />
                  <span className="text-xs sm:text-sm text-gray-600">
                    <Link to="/privacy" className="text-blue-600 hover:underline">
                      개인정보 처리방침
                    </Link>
                    {' 및 '}
                    <br className="sm:hidden" />
                    <button
                      type="button"
                      onClick={() => setShowMarketingModal(true)}
                      className="text-blue-600 hover:underline"
                    >
                      마케팅 정보 수신
                    </button>
                    {' 에\u00A0동의합니다. '}
                    <span className="text-red-500">*</span>
                    <span className="block text-xs text-gray-500 mt-1">
                      사례집 발송을 위해 필수로 동의가 필요합니다.
                    </span>
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 sm:py-4 rounded-lg font-bold text-base sm:text-lg hover:shadow-lg transition-all duration-300 flex items-center justify-center group relative overflow-hidden disabled:opacity-50"
              >
                <span className="relative z-10 flex items-center">
                  {isSubmitting ? '전송 중...' : '지금 이메일로 받기'}
                  {!isSubmitting && <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2 group-hover:translate-x-1 transition-transform" />}
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>

              {/* 추가 혜택 표시 */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4 mt-4 sm:mt-6">
                <div className="flex items-center text-xs sm:text-sm text-gray-600">
                  <Mail className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                  <span>이메일로 즉시 발송</span>
                </div>
                <div className="flex items-center text-xs sm:text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                  <span>29페이지 PDF</span>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* 마케팅 정보 수신 동의 모달 */}
      {showMarketingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* 모달 헤더 */}
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg sm:text-2xl font-bold text-gray-900">마케팅 정보 수신 동의</h3>
                <button
                  onClick={() => setShowMarketingModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500" />
                </button>
              </div>
            </div>

            {/* 모달 내용 */}
            <div className="p-4 sm:p-6 overflow-y-auto flex-1">
              <div className="space-y-4 sm:space-y-6">
                {/* 수집 항목 */}
                <div>
                  <h4 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 flex items-center text-gray-900">
                    <Database className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-600 flex-shrink-0" />
                    수집하는 개인정보 항목
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                    <ul className="space-y-2 text-sm sm:text-base text-gray-700">
                      <li className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>이메일 주소</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>이름</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>회사명</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* 이용 목적 */}
                <div>
                  <h4 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 flex items-center text-gray-900">
                    <Target className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-600 flex-shrink-0" />
                    개인정보 이용 목적
                  </h4>
                  <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
                    <ul className="space-y-2 text-sm sm:text-base text-gray-700">
                      <li className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>사례집 및 마케팅 자료 발송</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>서비스 및 이벤트 안내</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* 보유 기간 */}
                <div>
                  <h4 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 flex items-center text-gray-900">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-600 flex-shrink-0" />
                    개인정보 보유 및 이용 기간
                  </h4>
                  <div className="bg-yellow-50 rounded-lg p-3 sm:p-4">
                    <p className="text-sm sm:text-base text-gray-700">
                      <span className="font-semibold">수신 동의 철회 시까지</span>
                    </p>
                  </div>
                </div>

                {/* 수신 거부 권리 */}
                <div>
                  <h4 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 flex items-center text-gray-900">
                    <Shield className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-600 flex-shrink-0" />
                    수신 거부 안내
                  </h4>
                  <div className="bg-green-50 rounded-lg p-3 sm:p-4">
                    <p className="text-sm sm:text-base text-gray-700">
                      언제든지 수신 거부가 가능하며, 모든 이메일에 수신 거부 링크가 포함됩니다.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 모달 푸터 */}
            <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <p className="text-xs sm:text-sm text-gray-600">
                  위 내용을 확인하였으며, 마케팅 정보 수신에 동의합니다.
                </p>
                <button
                  onClick={() => {
                    setDownloadForm({ ...downloadForm, agreeToTerms: true });
                    setShowMarketingModal(false);
                  }}
                  className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm sm:text-base whitespace-nowrap"
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CaseStudiesLanding;